import React from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Col, Form, Input, Modal, Row, Select, Spin, message } from 'antd';
import PassengerUpdateNotice from '@/components/app/PassengerUpdateNotice';
import { formatDisplayDate } from '@/config';
import CityInput from '@/components/FormItems/CityInput';
import CountryInput from '@/components/FormItems/CountryInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import RegionInput from '@/components/FormItems/RegionInput';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { GenderType } from '@/constants';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';
import { PassengerDetailModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  ticketId: string;
  onClose: (reload: boolean) => void;
};

export default function UpdatePassengerModal({ ticketId, onClose }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [passengerDetail, setPassengerDetail] = React.useState<PassengerDetailModel>();
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);

  React.useEffect(() => {
    if (ticketId) {
      apiSdk.BookingDetailService.getTicketPassenger({ ticketId: ticketId })
        .then(res => {
          setPassengerDetail(res);
        })
        .catch(message.error)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [ticketId]);

  React.useEffect(() => {
    if (passengerDetail) {
      form.setFieldsValue(passengerDetail);
    }
  }, [passengerDetail]);

  // Identité figée du billet (vente) : affichée à côté de la fiche quand elles diffèrent.
  const holder = passengerDetail;
  const holderDiffers =
    !!holder &&
    (!!holder.holderFirstName || !!holder.holderLastName) &&
    (holder.holderFirstName !== holder.firstName ||
      holder.holderLastName !== holder.lastName ||
      (!!holder.holderDateOfBirth && formatDisplayDate(holder.holderDateOfBirth) !== formatDisplayDate(holder.dateOfBirth)) ||
      (holder.holderGender != null && holder.holderGender !== holder.gender));
  const identity = (first?: string, last?: string, born?: string | Date, gender?: number) =>
    [`${first ?? ''} ${last ?? ''}`.trim(), born ? formatDisplayDate(born) : null, gender != null ? GenderType.__getValue(gender) : null]
      .filter(Boolean)
      .join(' · ');

  const updatePassenger = (values: any) => {
    setLoading(true);
    apiSdk.BookingDetailService.updateTicketPassenger({ body: values })
      .then(res => {
        if (res.success) {
          onClose(true);
        } else {
          message.error(res.message);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.small}
      style={{ top: 10 }}
      className="nta-custom-form"
      title={t('manageTicket_UpdatePassenger')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            if (values.id) {
              updatePassenger(values);
            }
          })
          .catch(info => {});
      }}
    >
      <Spin spinning={loading}>
        <PassengerUpdateNotice />
        {holderDiffers ? (
          <Alert
            type="warning"
            showIcon
            className="mb-2"
            data-testid="ticket-holder-identity"
            message={t('ticketHolder_Differs')}
            description={
              <>
                <div>
                  {t('ticketHolder_Ticket')} : {identity(holder?.holderFirstName, holder?.holderLastName, holder?.holderDateOfBirth, holder?.holderGender)}
                </div>
                <div>
                  {t('ticketHolder_Record')} : {identity(holder?.firstName, holder?.lastName, holder?.dateOfBirth, holder?.gender)}
                </div>
              </>
            }
          />
        ) : null}
        <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('base.FirstName')} name="firstName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.LastName')} name="lastName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>

              <Form.Item label={t('base.DateOfBirth')} name="dateOfBirth" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <AppDatePicker className="w-100" />
              </Form.Item>
              <Form.Item
                label={t('base.PhoneCode')}
                name="phoneCode"
                rules={[{ required: passengerDetail?.isMainPassenger, message: VALIDATE_REQUIRED }]}
              >
                <PhoneCodeInput />
              </Form.Item>
              <Form.Item
                label={t('base.PhoneNumber')}
                name="phoneNumber"
                rules={[{ required: passengerDetail?.isMainPassenger, message: VALIDATE_REQUIRED }]}
              >
                <Input />
              </Form.Item>

              <Form.Item label={t('base.Email')} name="emailAddress">
                <Input />
              </Form.Item>
              <Form.Item label={t('base.Country')} name="countryId">
                <CountryInput
                  isOperatingOnly={false}
                  onChange={() => {
                    form.setFieldValue('regionId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.Region')} name="regionId">
                <RegionInput
                  disabled={!countryId}
                  countryId={countryId}
                  onChange={() => {
                    form.setFieldValue('cityId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.City')} name="cityId">
                <CityInput regionId={regionId} disabled={!regionId} />
              </Form.Item>

              <Form.Item label={t('base.Gender')} name="gender" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Select options={GenderType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item colon={false} label="   ">
                <Checkbox checked={passengerDetail?.isMainPassenger}>{t('manageTicket_IsMainPassenger')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
